import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Check, ArrowLeft, Zap } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/errors";

const PLANS = [
  { key: "solo_monthly", name: "Solo", price: "24,90 €", unit: "/mois", seats: false,
    features: ["1 utilisateur", "Capture OCR / NFC / manuel", "Débriefs vocaux IA", "Export PDF & relance email"] },
  { key: "equipe_monthly", name: "Équipe", price: "21,90 €", unit: "/licence/mois", seats: true, popular: true,
    features: ["Tout Solo +", "Collaboration & dashboard manager", "Synchro CRM HubSpot & Salesforce"] },
  { key: "entreprise_monthly", name: "Entreprise", price: "19,90 €", unit: "/licence/mois", seats: true,
    features: ["Tout Équipe +", "Archivage automatique CRM", "Comparateur différentiel IA", "Synthèse de compte"] },
];

export default function SubscribePage() {
  const [loadingKey, setLoadingKey] = useState("");
  const [seats, setSeats] = useState({ equipe_monthly: 2, entreprise_monthly: 10 });

  const subscribe = async (plan) => {
    setLoadingKey(plan.key);
    try {
      const quantity = plan.seats ? Math.max(plan.key === "entreprise_monthly" ? 10 : 2, seats[plan.key] || 1) : 1;
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.key, quantity, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoadingKey("");
    }
  };

  return (
    <div className="min-h-screen bg-background p-6 sm:p-10" data-testid="subscribe-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Choisissez votre offre</h1>
        <p className="text-muted-foreground mt-2 text-sm">Tarif dégressif par licence. Sans engagement, annulable à tout moment.</p>
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {PLANS.map((p) => (
            <div key={p.key} data-testid={`plan-card-${p.key}`} className={`rounded-2xl border p-6 flex flex-col ${p.popular ? "border-primary ring-1 ring-primary/30" : "border-border"}`}>
              {p.popular && <span className="self-start text-[11px] font-semibold uppercase tracking-wider text-primary bg-primary/10 rounded-full px-2.5 py-1 mb-3">Populaire</span>}
              <h2 className="font-heading text-xl font-bold">{p.name}</h2>
              <div className="mt-2"><span className="font-heading text-3xl font-extrabold">{p.price}</span> <span className="text-sm text-muted-foreground">{p.unit}</span></div>
              <ul className="mt-4 space-y-2 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground/80"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              {p.seats && (
                <div className="mt-4">
                  <label className="text-xs text-muted-foreground">Nombre de licences</label>
                  <Input type="number" min={p.key === "entreprise_monthly" ? 10 : 2} value={seats[p.key]}
                    onChange={(e) => setSeats({ ...seats, [p.key]: parseInt(e.target.value || "1", 10) })}
                    data-testid={`seats-input-${p.key}`} className="mt-1 h-9" />
                </div>
              )}
              <Button onClick={() => subscribe(p)} disabled={!!loadingKey} data-testid={`subscribe-btn-${p.key}`}
                className="mt-5 w-full h-11 rounded-xl font-semibold">
                {loadingKey === p.key ? <Loader2 className="w-4 h-4 klt-spin" /> : <><Zap className="w-4 h-4 mr-2" fill="currentColor" /> S'abonner</>}
              </Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-6 text-center">Paiement sécurisé par Stripe. Carte de test : 4242 4242 4242 4242.</p>
      </div>
    </div>
  );
}
